import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, Platform, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import Storage from '../../../../utils/Storage';
import { useProducts } from '../../../../context/ProductContext';
import { useNavigation } from '@react-navigation/native';

const HistorialDeCompra = () => {
  const navigation = useNavigation();
  const { setProductos, setSearchText, setDepartmentId, setCategoryId, setSubcategoryId, 
    setDepartmentName, setCategoryName, setSubcategoryName } = useProducts();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [productosDetalle, setProductosDetalle] = useState([]);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);

  const obtenerAuthYSucursal = async () => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return {
          mail: localStorage.getItem('mail') || '',
          codUsu: localStorage.getItem('codUsu') || '',
          suc: localStorage.getItem('sucursalId') || '4',
        };
      }
      return {
        mail: (await Storage.getItem('mail')) || '',
        codUsu: (await Storage.getItem('codUsu')) || '',
        suc: (await Storage.getItem('sucursalId')) || '4',
      };
    } catch (e) {
      return { mail: '', codUsu: '', suc: '4' };
    }
  };

  const fetchHistorial = async () => {
    setLoading(true);
    setError(null);
    try {
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        setError('Iniciá sesión para ver tu historial de compras.');
        setHistorial([]);
        return;
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);

      const url = `${BACKEND_URL}/rest/productos/historial?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Intentar parsear JSON de forma segura
      const text = await response.text();
      let data = null;
      try { data = JSON.parse(text); } catch {}

      const ds = data?.dsResultado || data?.response?.dsResultado?.dsResultado || null;

      // Estructuras posibles: ds.historial, ds.pedidos, ds.productos
      const items = Array.isArray(ds?.historial)
        ? ds.historial
        : Array.isArray(ds?.pedidos)
        ? ds.pedidos
        : Array.isArray(ds?.productos)
        ? ds.productos
        : [];

      setHistorial(items);
    } catch (err) {
      setError('No pudimos obtener tu historial. Intentalo más tarde.');
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistorial();
  }, []);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '-';
    const [year, month, day] = fechaStr.split('-');
    return `${day}/${month}/${year.slice(2)}`;
  };

  const formatMonto = (monto) => {
    if (monto === undefined || monto === null) return '-';
    return `$ ${parseFloat(monto).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchDetalleCompra = async (codCompra) => {
    setLoadingDetalle(true);
    try {
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        console.error('No hay sesión activa');
        return;
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);
      params.append('codCompra', codCompra);

      const url = `${BACKEND_URL}/rest/productos/historial/productos?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      let data = null;
      try { data = JSON.parse(text); } catch {}

      const ds = data?.dsResultado || null;
      const productos = Array.isArray(ds?.productos) ? ds.productos : [];

      const faltantes = Array.isArray(ds?.productosFaltantes)
        ? ds.productosFaltantes.map((p) => ({
            ...p,
            faltante: true,
            precio: p.precio ?? 0,
            precioSinImp: p.precioSinImp ?? 0,
            stock: p.stock ?? 0,
            maxStock: p.maxStock ?? 0,
            minStock: p.minStock ?? 1,
            oferta: p.oferta ?? '',
            cuotasSinInt: p.cuotasSinInt ?? 0,
            fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
          }))
        : [];

      const combined = [...productos, ...faltantes];

     
      setProductosDetalle(combined);
      setCompraSeleccionada(codCompra);

      // Limpiar contexto de búsqueda y categorías
      setSearchText('');
      setDepartmentId('0');
      setCategoryId('0');
      setSubcategoryId('0');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');

      // Pasar productos al contexto
      setProductos(combined);

      // Navegar a /productos
      if (Platform.OS === 'web' && window) {
        const url = `/productos?historial=true&codCompra=${encodeURIComponent(String(codCompra))}`;
        const isOnProductsPage = window.location.pathname === '/productos';
        
        if (isOnProductsPage) {
          // Si ya estamos en productos, solo actualizar la URL y disparar evento
          window.history.pushState({}, '', url);
          window.dispatchEvent(new Event('navigationChange'));
        } else {
          // Si estamos en otra página (como el perfil), navegar usando window.location
          window.location.href = url;
        }
      } else if (navigation) {
        navigation.navigate('Productos', { historial: true, codCompra });
      }
    } catch (err) {
      console.error('Error al obtener detalle de compra:', err);
    } finally {
      setLoadingDetalle(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}> 
        <ActivityIndicator size="large" color={colores.rojo} />
        <Text style={styles.loadingText}>Cargando historial...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <MaterialCommunityIcons name="alert-circle-outline" size={40} color={colores.error} />
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!historial || historial.length === 0) {
    return (
      <View style={styles.center}>
        <MaterialCommunityIcons name="clipboard-text-outline" size={40} color={colores.grisClaro} />
        <Text style={styles.emptyText}>No tenés compras registradas todavía.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={{ width: '100%' }}>
        {/* Header de la tabla */}
        <View style={styles.tablaHeader}>
          <Text style={[styles.tablaColumna, styles.fechaColumna]}>Fecha</Text>
          <Text style={[styles.tablaColumna, styles.cantArtColumna]}>Cant_Art.</Text>
          <Text style={[styles.tablaColumna, styles.montoColumna]}>Monto</Text>
          <Text style={[styles.tablaColumna, styles.compraColumna]}>Compra</Text>
          <Text style={[styles.tablaColumna, styles.verColumna]}>Ver</Text>
        </View>

        {/* Body de la tabla */}
        <ScrollView style={styles.tablaBody}>
          {historial.map((item, idx) => (
            <View key={idx} style={styles.tablaFila}>
              <Text style={[styles.tablaColumna, styles.fechaColumna, styles.textoCelda]}>
                {formatFecha(item.fechaCompra)}
              </Text>
              <Text style={[styles.tablaColumna, styles.cantArtColumna, styles.textoCelda]}>
                ({item.cantArt || 0} Art.)
              </Text>
              <Text style={[styles.tablaColumna, styles.montoColumna, styles.textoCelda]}>
                {formatMonto(item.total)}
              </Text>
              <Text style={[styles.tablaColumna, styles.compraColumna, styles.textoCelda]}>
                {item.lugar || 'Sucursal'}
              </Text>
              <View style={[styles.tablaColumna, styles.verColumna]}>
                <TouchableOpacity 
                  style={styles.botonVer}
                  onPress={() => fetchDetalleCompra(item.codCompra)}
                  disabled={loadingDetalle}
                >
                  <MaterialCommunityIcons name="eye" size={18} color={colores.rojo} />
                  <Text style={styles.textoVer}>Ver</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    marginTop: 10,
    color: colores.textoPrincipal,
    fontFamily: fuentes.regular,
  },
  errorText: {
    marginTop: 8,
    color: colores.error,
    fontFamily: fuentes.regular,
  },
  emptyText: {
    marginTop: 8,
    color: colores.texto,
    fontFamily: fuentes.regular,
  },

  tablaHeader: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
    width: '100%',
  },
  tablaBody: {
    maxHeight: 400,
    width: '100%',
  },
  tablaFila: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    width: '100%',
  },
  tablaColumna: {
    fontFamily: fuentes.semiBold,
    fontSize: 13,
    color: colores.textoPrincipal,
  },
  textoCelda: {
    fontFamily: fuentes.regular,
    fontSize: 13,
    color: colores.texto,
  },
  fechaColumna: {
    flex: 2,
    textAlign: 'left',
  },
  cantArtColumna: {
    flex: 2,
    textAlign: 'center',
  },
  montoColumna: {
    flex: 2.5,
    textAlign: 'right',
  },
  compraColumna: {
    flex: 2,
    textAlign: 'center',
  },
  verColumna: {
    flex: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  botonVer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  textoVer: {
    marginLeft: 4,
    color: colores.rojo,
    fontFamily: fuentes.semiBold,
    fontSize: 12,
  },
});

export default HistorialDeCompra;
